import { sessionLocalStorageKey } from "@/global/config/constants";

import type { PacksList } from "@/types";
import type { EventType, UserType } from "@/global/store/data/types";
import { TeamConfig } from "@/global/hooks/useTeamConfig";
import { calcMinMaxEventCountRequired } from "../utils.events";

// prettier-ignore
export function handleSetUpdatedAccountGroup(accountGroup: string[], teamSchema: string) {
  const storageKey = `${sessionLocalStorageKey}-${teamSchema}-updatedAccountGroup`;
  window.localStorage.removeItem(storageKey);
  window.localStorage.setItem(storageKey, JSON.stringify(accountGroup)); // setting updated account group to local storage
}

// prettier-ignore
export function handleGetUpdatedAccountGroup(teamSchema: string): string[] | undefined {
  let result : string[] = [];
  try{
    const storageKey = `${sessionLocalStorageKey}-${teamSchema}-updatedAccountGroup`;
    result = JSON.parse(window.localStorage.getItem(storageKey) || "[]");
    
    if(Array.isArray(result) && result?.length) {
      result = result.filter(itm => {
        return itm !== undefined && itm !== null && itm !== "undefined";
      });
    };
    
  } catch (error) {
    console.log("Error getting updated account group", error);
  } finally {
    return result;
  }
  
}

export function handleRemoveUpdatedAccountGroup(teamSchema: string) {
  window.localStorage.removeItem(sessionLocalStorageKey + "-" + teamSchema + "-updatedAccountGroup"); // prettier-ignore
}

// prettier-ignore
export function handlePackDynamicAccountGroup(user: UserType, pack: PacksList, teamSchema: string) {
  const userAccountGroup = user?.accountGroupIds;
  const dynamicAccountGroup = pack?.dynamicAccountGroup;
  if (dynamicAccountGroup && userAccountGroup) {
    const sortedGroups = [...dynamicAccountGroup].sort((a, b) => a.priority - b.priority);
    for (const group of sortedGroups) {
      if (userAccountGroup.some(userAccountGroup => group.originalAccountGroupIds.includes(userAccountGroup))) {
        return handleSetUpdatedAccountGroup(group.updatedAccountGroupId, teamSchema);
      }
    }
  }
}

// returns an array of events that are not in the pack bonus
export function findEventsExceptPackBonus(
  events?: EventType[],
  pack?: PacksList
) {
  if (!events?.length || !pack) return [];

  const bonusEventCodes = pack?.unlockBonus?.bonusEventCodes || [];

  const filteredEvents = events?.filter(
    (event) =>
      !bonusEventCodes.some((eventCode) => eventCode === event.eventCode)
  );

  return filteredEvents;
}

// returns an array of events that are in the pack bonus
export function findPackBonusEvents(events?: EventType[], pack?: PacksList) {
  if (!events?.length || !pack) return [];
  const bonusEventCodes = pack?.unlockBonus?.bonusEventCodes || [];

  const bonusEvents =
    events?.filter((event) => bonusEventCodes.includes(event.eventCode)) || [];

  return bonusEvents;
}

// returns an array of events that are not in the all packs bonus
export function findEventsExceptAllPacksBonus(
  events?: EventType[],
  teamConfig?: TeamConfig
) {
  if (!events?.length) return [];
  const bonusEventCodes: string[] = [];

  const configPacks = teamConfig?.packs?.list || [];
  if (!configPacks?.length) return events;

  for (const pack of configPacks) {
    bonusEventCodes.push(...(pack?.unlockBonus?.bonusEventCodes || []));
  }

  const filteredEvents =
    events?.filter((event) => !bonusEventCodes.includes(event.eventCode)) || [];

  return filteredEvents;
}

// returns true if the event is in the pack bonus
export function isPackBonusEvent(event?: EventType, pack?: PacksList) {
  if (!event || !pack) return false;
  const bonusEventCodes = pack?.unlockBonus?.bonusEventCodes || [];
  return bonusEventCodes.includes(event?.eventCode || "");
}

// returns an array of events that are unlocked by the pack bonus
export function findUnlockedBonusEvents(args: {
  teamConfig: TeamConfig;
  events: EventType[];
  selectedEvents: EventType[];
  selectedPack?: PacksList;
  selectedBucket?: PacksList["buckets"][0];
}) {
  const { teamConfig, events, selectedPack, selectedBucket } = args;

  let unlocked: EventType[] = [];

  const { minEventCountRequired } = calcMinMaxEventCountRequired(
    teamConfig,
    selectedBucket,
    null
  );

  const bonusEvents = findPackBonusEvents(events, selectedPack);

  const selectedEvents = findEventsExceptPackBonus(
    args?.selectedEvents,
    selectedPack
  );

  const isMinCountReached = selectedEvents?.length >= minEventCountRequired;
  if (!isMinCountReached) return unlocked;
  unlocked = bonusEvents;
  return unlocked;
}

// returns an array of events that are not accepted by the pack bonus
export function findUnacceptedBonusEventsSelected(args: {
  teamConfig: TeamConfig;
  selectedEvents: EventType[];
  selectedPack?: PacksList;
  selectedBucket?: PacksList["buckets"][0];
}) {
  const { teamConfig, selectedPack, selectedBucket } = args;

  let unacceptedEvents: EventType[] = [];

  const { minEventCountRequired } = calcMinMaxEventCountRequired(
    teamConfig,
    selectedBucket,
    null
  );

  const selectedEvents = findEventsExceptPackBonus(
    args?.selectedEvents,
    selectedPack
  );

  const isBelowMinCount = selectedEvents?.length < minEventCountRequired;

  const selectedBonusEvents = findPackBonusEvents(
    args?.selectedEvents,
    selectedPack
  );

  if (isBelowMinCount && selectedBonusEvents?.length > 0) {
    unacceptedEvents = selectedBonusEvents;
  }

  return unacceptedEvents;
}
